import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

interface TrafficJourneyProps {
  sources: { source: string; sessions: number }[]
  landingPages: { page: string; sessions: number }[]
  domain: string
}

export function TrafficJourney({ sources, landingPages, domain }: TrafficJourneyProps) {
  const option = useMemo(() => {
    if (!sources.length || !domain) return null

    // Prefix names to avoid collisions between sources, domain and pages
    const srcPrefix = 'src:'
    const pagePrefix = 'page:'
    const domainName = domain

    const nodes = [
      ...sources.map((s) => ({ name: srcPrefix + s.source, itemStyle: { color: '#6366F1' } })),
      { name: domainName, itemStyle: { color: '#1B2559' } },
      ...landingPages.map((p) => ({ name: pagePrefix + p.page, itemStyle: { color: '#10B981' } })),
    ]

    const links = [
      ...sources.filter(s => s.sessions > 0).map((s) => ({
        source: srcPrefix + s.source, target: domainName, value: s.sessions,
      })),
      ...landingPages.filter(p => p.sessions > 0).map((p) => ({
        source: domainName, target: pagePrefix + p.page, value: p.sessions,
      })),
    ]

    if (links.length === 0) return null

    return {
      tooltip: {
        trigger: 'item' as const,
        backgroundColor: '#1B2559',
        borderColor: '#1B2559',
        borderRadius: 8,
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          if (params.dataType === 'node') {
            const name = String(params.name).replace(/^(src:|page:)/, '')
            return `<b>${name}</b><br/>${params.value?.toLocaleString() || 0} sessions`
          }
          const src = String(params.data.source).replace(/^(src:|page:)/, '')
          const tgt = String(params.data.target).replace(/^(src:|page:)/, '')
          return `${src} &rarr; ${tgt}<br/>${params.value?.toLocaleString() || 0} sessions`
        },
      },
      series: [
        {
          type: 'sankey',
          layout: 'none',
          emphasis: { focus: 'adjacency' },
          nodeAlign: 'justify',
          nodeGap: 12,
          nodeWidth: 20,
          label: {
            color: '#1B2559',
            fontSize: 11,
            formatter: (params: any) => String(params.name).replace(/^(src:|page:)/, ''),
          },
          lineStyle: {
            color: 'gradient',
            curveness: 0.5,
            opacity: 0.4,
          },
          itemStyle: {
            borderWidth: 0,
          },
          data: nodes,
          links: links,
        },
      ],
    }
  }, [sources, landingPages, domain])

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-[#1B2559] mb-4">Parcours du trafic</h3>
      {option ? (
        <ReactECharts option={option} style={{ height: '350px' }} />
      ) : (
        <div className="flex items-center justify-center" style={{ height: '200px' }}>
          <p className="text-sm text-[#8B8FA7]">Aucune donn&eacute;e de parcours disponible</p>
        </div>
      )}
    </div>
  )
}
